/**
 * Marketplace page — sector performance, coverage heatmap, block generator.
 *
 * Renders an embedded Leaflet-style SVG map (we use simple SVG over the
 * sector lat/lng polygons to keep us bundle-free) showing every sector
 * tinted by health and the Block Generator panel pinned to the right.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw, PlayCircle, Map, Layers, Sparkles, ExternalLink,
  Activity, Loader2, Users,
} from "lucide-react";

import { api, formatApiError } from "@/shared/lib/api";
import AdminShell from "@/apps/admin/v2/AdminShell";
import { Card, CardHeader, PillButton } from "@/apps/admin/v2/components/Card";
import SectorPolygonMap from "@/apps/admin/v2/components/SectorPolygonMap";

const HEALTH_TONE = {
  oversupplied: { bg: "bg-emerald-50", ring: "ring-emerald-200", dot: "bg-emerald-500", label: "Oversupplied" },
  balanced:     { bg: "bg-yellow-50",  ring: "ring-yellow-200",  dot: "bg-yellow-500",  label: "Balanced" },
  tight:        { bg: "bg-orange-50",  ring: "ring-orange-200",  dot: "bg-orange-500",  label: "Tight" },
  undersupplied:{ bg: "bg-red-50",     ring: "ring-red-200",     dot: "bg-red-500",     label: "Undersupplied" },
  inactive:     { bg: "bg-slate-50",   ring: "ring-slate-200",   dot: "bg-slate-400",   label: "Inactive" },
};

export default function MarketplacePage() {
  const [sectors, setSectors] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [blocksSummary, setBlocksSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSector, setSelectedSector] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [lastGen, setLastGen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h, b] = await Promise.all([
        api.get("/admin/v2/marketplace/sectors-performance"),
        api.get("/admin/v2/marketplace/heatmap"),
        api.get("/admin/blocks/summary"),
      ]);
      setSectors(s.data.sectors || []);
      setHeatmap(h.data.items || []);
      setBlocksSummary(b.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load marketplace");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/blocks/generate", {});
      setLastGen(data);
      const promoted = data.paid_riders_promoted || 0;
      const promoBit = promoted > 0 ? ` (auto-approved ${promoted} paid rider${promoted === 1 ? "" : "s"})` : "";
      toast.success(`Generated ${data.blocks_created} block(s) from ${data.trips_scanned} trip(s)${promoBit}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  // Driver visibility diagnostic
  const [diagEmail, setDiagEmail] = useState("");
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const runDiagnostic = useCallback(async () => {
    const email = (diagEmail || "").trim().toLowerCase();
    if (!email) return;
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const r = await api.get(`/admin/diagnostics/driver-visibility?email=${encodeURIComponent(email)}`);
      setDiagResult(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Diagnostic failed");
    } finally {
      setDiagLoading(false);
    }
  }, [diagEmail]);

  const repairOrphans = useCallback(async () => {
    setRepairing(true);
    try {
      const { data } = await api.post("/admin/diagnostics/repair-orphans");
      const fixed = data.orphaned_trips_fixed || 0;
      const created = data.generator_summary?.blocks_created || 0;
      toast.success(`Repaired ${fixed} orphan trip(s) · generated ${created} new block(s)`);
      // Re-run the diagnostic so the operator immediately sees the result
      if (diagEmail.trim()) await runDiagnostic();
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Repair failed");
    } finally {
      setRepairing(false);
    }
  }, [diagEmail, runDiagnostic, load]);

  const activeSectors = useMemo(() => sectors.filter((s) => s.active), [sectors]);
  const inactiveCount = useMemo(() => sectors.length - activeSectors.length, [sectors, activeSectors]);
  const totalOpen = useMemo(() => sectors.reduce((acc, s) => acc + (s.metrics?.open_blocks || 0), 0), [sectors]);
  const totalClaimed = useMemo(() => sectors.reduce((acc, s) => acc + (s.metrics?.claimed_blocks || 0), 0), [sectors]);

  return (
    <AdminShell
      title="Marketplace"
      subtitle="Supply, demand, sector coverage. Generate blocks from rider demand and watch them flow into driver inventory."
      headerActions={
        <>
          <Link to="/admin/v2/settings/sectors">
            <PillButton variant="secondary" testid="market-edit-sectors">
              <ExternalLink size={13} /> Edit sectors
            </PillButton>
          </Link>
          <PillButton onClick={load} variant="secondary" testid="market-refresh">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </PillButton>
          <PillButton onClick={onGenerate} disabled={generating} variant="primary" testid="market-generate">
            <PlayCircle size={13} /> {generating ? "Generating…" : "Generate blocks"}
          </PillButton>
        </>
      }
    >
      {/* Strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Stat label="Active Sectors" value={activeSectors.length} testid="market-stat-active" />
        <Stat label="Inactive" value={inactiveCount} testid="market-stat-inactive" />
        <Stat label="Open Blocks" value={totalOpen} testid="market-stat-open" />
        <Stat label="Claimed / In-Progress" value={totalClaimed} testid="market-stat-claimed" />
        <Stat label="Coverage" value={
          activeSectors.length === 0 ? "0%" :
          `${Math.round((activeSectors.filter((s) => (s.metrics?.open_blocks || 0) > 0).length / activeSectors.length) * 100)}%`
        } testid="market-stat-coverage" />
      </div>

      {/* Driver/Rider visibility diagnostic */}
      <Card className="mb-5" testid="market-diagnostic">
        <CardHeader eyebrow="Diagnostic" title="Why doesn't this driver see blocks? · Why didn't this rider's trips generate?" action={
          <span className="text-[10px] tracking-[0.15em] uppercase text-[#475569]">
            <Activity size={11} className="inline mr-1" /> Visibility
          </span>
        } />
        <div className="p-4">
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="email"
              placeholder="driver@email.com"
              value={diagEmail}
              onChange={(e) => setDiagEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runDiagnostic(); }}
              className="flex-1 min-w-[220px] h-10 px-3 rounded-full border border-[#0F172A]/15 text-[13px] text-[#0F172A] placeholder-[#9AA3B7] focus:outline-none focus:border-[#ed6500]"
              data-testid="market-diag-email"
            />
            <button
              type="button"
              onClick={runDiagnostic}
              disabled={diagLoading || !diagEmail.trim()}
              className="h-10 px-5 rounded-full bg-[#0F172A] text-white text-[11px] tracking-[0.18em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="market-diag-check"
            >
              {diagLoading ? <Loader2 size={14} className="animate-spin" /> : <Activity size={14} />} Check visibility
            </button>
          </div>
          {diagResult && (
            <div className="mt-4 space-y-3" data-testid="market-diag-result">
              <div className={`rounded-2xl p-3 text-[12.5px] ${diagResult.diagnosis?.includes("nothing wrong") || (diagResult.will_pass_driver_gate && diagResult.visible_to_driver.total > 0) ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-900 border border-amber-200"}`}>
                <div className="font-semibold uppercase tracking-[0.16em] text-[10px] mb-1">
                  Diagnosis · {diagResult.user_kind || "unknown"}
                </div>
                {diagResult.diagnosis}
              </div>
              {/* Quick repair CTA when orphans / no_block are involved */}
              {(diagResult.diagnosis?.includes("orphan") || diagResult.diagnosis?.includes("no block_id") || diagResult.diagnosis?.includes("Generate blocks")) && (
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={repairOrphans}
                    disabled={repairing}
                    className="h-9 px-4 rounded-full bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[11px] tracking-[0.18em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="market-diag-repair"
                  >
                    {repairing ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />} Repair orphans & regenerate
                  </button>
                  <button
                    type="button"
                    onClick={onGenerate}
                    disabled={generating}
                    className="h-9 px-4 rounded-full bg-[#0F172A] text-white text-[11px] tracking-[0.18em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="market-diag-generate"
                  >
                    {generating ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />} Generate blocks
                  </button>
                </div>
              )}
              {/* Driver-mode gates (only meaningful when user IS a driver) */}
              {diagResult.user_kind === "driver" && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11.5px]">
                  <DiagCell label="User exists" value={diagResult.gates.user_exists ? "Yes" : "No"} good={diagResult.gates.user_exists} />
                  <DiagCell label="is_driver" value={diagResult.gates.is_driver ? "true" : "false"} good={diagResult.gates.is_driver} />
                  <DiagCell label="is_active" value={diagResult.gates.is_active ? "true" : "false"} good={diagResult.gates.is_active} />
                  <DiagCell label="access_status" value={diagResult.gates.access_status || "—"} good={!diagResult.gates.access_status || diagResult.gates.access_status === "APPROVED"} />
                </div>
              )}
              {/* Rider-mode per-trip table */}
              {diagResult.user_kind === "rider" && diagResult.rider_trips?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#475569] mb-1">
                    This rider&rsquo;s trips in next 14 days · {diagResult.rider_trips.length} total
                  </div>
                  <div className="bg-[#F9F7F4] rounded-xl border border-[#0F172A]/[0.05] divide-y divide-[#0F172A]/[0.05]">
                    {diagResult.rider_trips.map((t) => {
                      const ok = t.status_label === "linked";
                      const bad = ["orphaned_block_id", "no_block", "block_cancelled"].includes(t.status_label);
                      return (
                        <div key={t.trip_id} className="px-3 py-2 flex items-start justify-between gap-2 text-[11.5px]" data-testid={`market-diag-trip-${t.trip_id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="text-[#0F172A] font-medium">
                              {t.date} · {t.pickup_time} · {t.direction}
                            </div>
                            <div className="text-[#475569] truncate">{t.pickup_address || "—"}</div>
                            <div className={`text-[10.5px] mt-0.5 ${ok ? "text-emerald-700" : bad ? "text-red-700" : "text-[#475569]"}`}>
                              {t.reason}
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-[9.5px] tracking-[0.12em] uppercase font-semibold whitespace-nowrap ${ok ? "bg-emerald-100 text-emerald-700" : bad ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}>
                            {t.status_label.replace(/_/g, " ")}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11.5px]">
                <DiagCell label="Blocks visible to drivers (14d)" value={diagResult.visible_to_driver.total} good={diagResult.visible_to_driver.total > 0} />
                <DiagCell label="Window" value={`${diagResult.window.from} → ${diagResult.window.to}`} good />
                {diagResult.user_kind === "driver" && (
                  <DiagCell label="Will pass driver gate" value={diagResult.will_pass_driver_gate ? "Yes" : "No"} good={diagResult.will_pass_driver_gate} />
                )}
              </div>
              {diagResult.inventory_breakdown?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#475569] mb-1">Inventory in next 14 days</div>
                  <div className="flex flex-wrap gap-1.5">
                    {diagResult.inventory_breakdown.map((r, i) => (
                      <span key={i} className="px-2 py-1 rounded-full bg-[#F1ECE3] text-[#0F172A] text-[10.5px]">
                        {r.source || "—"} · {r.status} · {r.count}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {diagResult.visible_to_driver.sample?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#475569] mb-1">Sample visible blocks</div>
                  <div className="bg-[#F9F7F4] rounded-xl border border-[#0F172A]/[0.05] divide-y divide-[#0F172A]/[0.05]">
                    {diagResult.visible_to_driver.sample.map((b) => (
                      <div key={b.id} className="px-3 py-2 flex items-center justify-between text-[11.5px]" data-testid={`market-diag-sample-${b.id}`}>
                        <span className="text-[#0F172A]">{b.date} · {b.window_start}–{b.window_end} · {b.sector_name}</span>
                        <span className="text-[#475569]">${b.pay_dollars} · {b.legs_count} leg{b.legs_count === 1 ? "" : "s"} · {b.source}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Map */}
        <Card className="xl:col-span-2 overflow-hidden" testid="market-map-card">
          <CardHeader eyebrow="Coverage Heatmap" title="Sector boundaries" action={
            <div className="flex flex-wrap items-center gap-3 text-[10px] tracking-[0.15em] uppercase text-[#475569]">
              {Object.entries(HEALTH_TONE).map(([k, t]) => (
                <span key={k} className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${t.dot}`} /> {t.label}
                </span>
              ))}
            </div>
          } />
          <div className="p-3">
            <SectorPolygonMap
              items={heatmap}
              onSelect={(id) => setSelectedSector(id)}
              selectedId={selectedSector}
              height={460}
            />
          </div>
        </Card>

        {/* Sector list */}
        <Card testid="market-sectors-card">
          <CardHeader eyebrow="Sector Performance" title="Live snapshot" />
          <div className="px-2 py-2 max-h-[520px] overflow-y-auto" data-testid="market-sectors-list">
            {loading ? (
              <div className="text-center py-12 text-[#475569] text-[13px]">Loading…</div>
            ) : sectors.length === 0 ? (
              <div className="text-center py-12 text-[#475569] text-[13px]">No sectors yet.</div>
            ) : sectors.map((s) => {
              const tone = HEALTH_TONE[s.health] || HEALTH_TONE.inactive;
              const isSel = selectedSector === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSelectedSector(isSel ? null : s.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-xl ring-1 ${tone.ring} ${tone.bg} mb-1.5 hover:opacity-90 ${isSel ? "outline outline-2 outline-[#F97316]" : ""}`}
                  data-testid={`market-sector-${s.slug}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
                      <span className="font-semibold text-[#081B4B] text-[13px] truncate">{s.name}</span>
                      {!s.active && <span className="text-[9px] tracking-wider uppercase text-[#475569]">Inactive</span>}
                      {s.manual && <span className="text-[9px] tracking-wider uppercase text-[#F97316]">Manual</span>}
                    </div>
                    <span className="text-[10px] tracking-wider uppercase text-[#475569]">{tone.label}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-2 text-[11px] text-[#081B4B]">
                    <Pair label="Open" v={s.metrics.open_blocks} />
                    <Pair label="Claimed" v={s.metrics.claimed_blocks} />
                    <Pair label="Today" v={s.metrics.blocks_today} />
                    <Pair label="Earned" v={`$${(s.metrics.revenue_completed_dollars || 0).toFixed(0)}`} />
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] tracking-wider uppercase text-[#475569]">
                    <span>Weight {s.demand_weight.toFixed(1)}×</span>
                    <span>Surge {s.surge_multiplier.toFixed(2)}×</span>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Block Generator panel */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Card className="lg:col-span-2" testid="market-block-generator">
          <CardHeader eyebrow="Auto Block Generator" title="Convert rider demand → driver inventory" action={
            <PillButton onClick={onGenerate} disabled={generating} variant="dark" testid="market-gen-now">
              <PlayCircle size={13} /> {generating ? "Generating…" : "Generate now"}
            </PillButton>
          } />
          <div className="p-5">
            {!lastGen ? (
              <div className="text-[13px] text-[#475569]">
                Click <strong>Generate now</strong> to bucket scheduled rider trips into available driver blocks using sector + time-window assignments.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-[#081B4B]">
                <RunStat label="Scanned" v={lastGen.trips_scanned} />
                <RunStat label="Assigned" v={lastGen.trips_assigned} />
                <RunStat label="Created" v={lastGen.blocks_created} />
                <RunStat label="Out of area" v={lastGen.out_of_area} />
                <RunStat label="Low demand" v={lastGen.low_demand_buckets} />
              </div>
            )}
            {blocksSummary && (
              <div className="mt-5 pt-5 border-t border-[#0F172A]/[0.06]">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#475569] mb-2 flex items-center gap-1.5">
                  <Layers size={11} /> Inventory mix
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(blocksSummary.by_source || {}).map(([k, n]) => (
                    <span key={k} className="px-2.5 py-1 rounded-full text-[11px] bg-[#081B4B] text-white">
                      {k} · {n}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card testid="market-coverage-tip">
          <CardHeader eyebrow="What this map shows" title="Reading the heatmap" />
          <div className="p-5 text-[12px] text-[#475569] space-y-2">
            <p><strong className="text-[#081B4B]">Oversupplied</strong> sectors have lots of open driver blocks but few claims. Investigate marketing pushes.</p>
            <p><strong className="text-[#081B4B]">Balanced</strong> sectors have parity between supply and demand — healthy state.</p>
            <p><strong className="text-[#081B4B]">Tight</strong> sectors have most blocks claimed and limited spare capacity.</p>
            <p><strong className="text-[#081B4B]">Undersupplied</strong> sectors have demand with no available blocks — generate more or recruit drivers.</p>
            <p className="pt-2 border-t border-[#0F172A]/[0.06] text-[#F97316]">
              <Sparkles size={12} className="inline mr-1" /> Sector boundaries, demand weight, and surge are editable from <Link to="/admin/v2/settings/sectors" className="underline">Sector Manager</Link>.
            </p>
          </div>
        </Card>
      </section>
    </AdminShell>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white rounded-xl border border-[#0F172A]/[0.08] p-4" data-testid={testid}>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#475569]">{label}</div>
      <div className="font-serif text-[26px] text-[#081B4B] mt-1">{value}</div>
    </div>
  );
}

function Pair({ label, v }) {
  return (
    <div>
      <div className="text-[9px] tracking-[0.15em] uppercase text-[#475569]">{label}</div>
      <div className="font-semibold">{v}</div>
    </div>
  );
}

function RunStat({ label, v }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#475569]">{label}</div>
      <div className="font-serif text-[22px] text-[#081B4B]">{v}</div>
    </div>
  );
}


function DiagCell({ label, value, good }) {
  return (
    <div className={`rounded-xl border p-2.5 ${good ? "bg-emerald-50/70 border-emerald-200" : "bg-red-50/70 border-red-200"}`}>
      <div className="text-[9.5px] uppercase tracking-[0.18em] text-[#475569]">{label}</div>
      <div className="text-[12px] text-[#0F172A] font-semibold mt-0.5 break-words">{String(value)}</div>
    </div>
  );
}
